import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Chart from "react-apexcharts";
import { ArrowLeft, RotateCcw } from "lucide-react";
import BONavigation from "../../../components/admin/BONavigation";
import { adminRestoreApiKey, adminRevokeApiKey } from "interfaces/apiKeys";
import { PublicApiAccountDetail, RateLimits, Throughput, estimateLimits, getPublicApiAccount, getPublicApiDeliveries, unlockPublicApiAccount, updatePublicApiAccount } from "interfaces/publicApi";
import { CallsTable, Card, FlagBadge, LIMIT_PRESETS, ProgressBar, REASON_LABELS, describeLimits, formatDateTime, formatDuration, formatRelative, n } from "./publicApi/components";

/**
 * /bo/public-api/:userId — one client of the public API, from A to Z: their call limiter
 * (set here, never shown to them) and where it stands right now, a chart of every call (what it
 * returned, how far it took them through the base, which calls were refused), what they called
 * and from where, the suspicious behaviour, and everything they received.
 */

const RANGES = [1, 7, 30, 90, 365];

function perCallOptions(): ApexCharts.ApexOptions {
    return {
        chart: {
            type: "line",
            toolbar: { show: true, tools: { download: false, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true } },
            zoom: { enabled: true, type: "x" },
            fontFamily: "inherit",
            background: "transparent",
            animations: { enabled: false },
        },
        stroke: { width: [0, 0, 0, 2], curve: "stepline" },
        markers: { size: [0, 5, 5, 0] },
        colors: ["#1A1A1A", "#f59e0b", "#ef4444", "#16a34a"],
        plotOptions: { bar: { columnWidth: "70%" } },
        dataLabels: { enabled: false },
        xaxis: { type: "datetime", labels: { datetimeUTC: false, style: { colors: "#6b7280", fontSize: "10px" } } },
        yaxis: [
            { seriesName: "Établissements renvoyés", title: { text: "Établissements / appel", style: { color: "#6b7280", fontSize: "11px" } }, labels: { style: { colors: "#6b7280", fontSize: "11px" } }, min: 0, forceNiceScale: true },
            { seriesName: "Établissements renvoyés", show: false },
            { seriesName: "Établissements renvoyés", show: false },
            { opposite: true, seriesName: "Part de la base reçue (%)", min: 0, max: 100, title: { text: "% de la base", style: { color: "#16a34a", fontSize: "11px" } }, labels: { formatter: (v: number) => `${Math.round(v)} %`, style: { colors: "#16a34a", fontSize: "11px" } } },
        ],
        grid: { borderColor: "#f3f4f6", strokeDashArray: 3 },
        tooltip: { shared: true, intersect: false, x: { format: "dd/MM HH:mm:ss" } },
        legend: { position: "top", horizontalAlign: "left", fontSize: "12px", labels: { colors: "#374151" } },
    };
}

function dailyOptions(labels: string[]): ApexCharts.ApexOptions {
    return {
        chart: { type: "bar", stacked: true, toolbar: { show: false }, fontFamily: "inherit", background: "transparent" },
        plotOptions: { bar: { borderRadius: 2, columnWidth: "60%" } },
        colors: ["#1A1A1A", "#ef4444", "#16a34a"],
        dataLabels: { enabled: false },
        xaxis: { categories: labels, labels: { style: { colors: "#6b7280", fontSize: "10px" } }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { labels: { style: { colors: "#6b7280", fontSize: "11px" } } },
        grid: { borderColor: "#f3f4f6", strokeDashArray: 3 },
        tooltip: { shared: true, intersect: false },
        legend: { position: "top", horizontalAlign: "left", fontSize: "12px", labels: { colors: "#374151" } },
    };
}

/** The limiter form: presets, every setting, and what it allows at most per day. */
function LimitsEditor({ userId, detail, onSaved }: { userId: string; detail: PublicApiAccountDetail; onSaved: () => void }) {
    const acc = detail.account;
    const toForm = (l?: RateLimits) => ({
        minIntervalSec: String(l?.minIntervalSec ?? 1),
        burst: String(l?.burst ?? 4),
        cooldownsMin: (l?.cooldownsMin ?? [1, 5]).join(", "),
        resetAfterMin: String(l?.resetAfterMin ?? 60),
        maxPerDay: String(l?.maxPerDay ?? 200),
        maxResults: String(l?.maxResults ?? 20),
    });
    const [form, setForm] = useState(toForm(acc?.limits));
    const [notes, setNotes] = useState(acc?.notes ?? "");
    const [saving, setSaving] = useState(false);
    const [estimate, setEstimate] = useState<(Throughput & { base: number }) | null>(null);
    const [formError, setFormError] = useState<string | null>(null);

    useEffect(() => {
        setForm(toForm(acc?.limits));
        setNotes(acc?.notes ?? "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(acc?.limits), acc?.notes]);

    const limits = (): RateLimits => ({
        minIntervalSec: Number(form.minIntervalSec),
        burst: Number(form.burst),
        cooldownsMin: form.cooldownsMin
            .split(",")
            .map((c) => c.trim())
            .filter(Boolean)
            .map(Number),
        resetAfterMin: Number(form.resetAfterMin),
        maxPerDay: Number(form.maxPerDay),
        maxResults: Number(form.maxResults),
    });

    // Live estimate as the form changes
    useEffect(() => {
        const t = setTimeout(async () => {
            const res = await estimateLimits(limits());
            if ("error" in res) {
                setFormError(res.error);
                setEstimate(null);
            } else {
                setFormError(null);
                setEstimate(res);
            }
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(form)]);

    const save = async (extra: Partial<{ enabled: boolean }> = {}) => {
        setSaving(true);
        const res = await updatePublicApiAccount(userId, { limits: limits(), notes, ...extra });
        setSaving(false);
        if (res.error) return window.alert(res.error);
        onSaved();
    };

    const toggle = async () => {
        const enabling = !acc?.enabled;
        if (!window.confirm(enabling ? "Activer l'accès API de ce client ?" : "Désactiver l'accès API ? Toutes ses clés seront refusées immédiatement (403).")) return;
        await save({ enabled: enabling });
    };

    const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const input = "w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-gray-400";
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-semibold text-gray-900">Limitation des appels</p>
                <button onClick={toggle} disabled={saving} className={`text-xs px-3 py-1.5 rounded-lg ${acc?.enabled ? "border border-red-200 text-red-600 hover:bg-red-50" : "bg-black text-white"}`}>
                    {acc?.enabled ? "Désactiver l'accès" : "Activer l'accès"}
                </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
                Invisible pour le client : il ne voit qu'une réponse 429 avec Retry-After quand un appel est refusé. Un changement s'applique dès son appel suivant.
            </p>
            <div className="flex flex-wrap gap-1.5 mb-3">
                {LIMIT_PRESETS.map((p) => (
                    <button key={p.label} onClick={() => setForm(toForm(p.limits))} className="text-[11px] px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 text-gray-700">
                        {p.label}
                    </button>
                ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                <label className="text-xs text-gray-600">
                    Délai minimum entre deux appels (s)
                    <input className={input} type="number" min={0} step="any" value={form.minIntervalSec} onChange={set("minIntervalSec")} />
                </label>
                <label className="text-xs text-gray-600">
                    Appels d'affilée avant une pause (0 = jamais)
                    <input className={input} type="number" min={0} value={form.burst} onChange={set("burst")} />
                </label>
                <label className="text-xs text-gray-600">
                    Pauses en minutes, qui s'allongent (ex. 1, 5)
                    <input className={input} value={form.cooldownsMin} onChange={set("cooldownsMin")} />
                </label>
                <label className="text-xs text-gray-600">
                    Remise à zéro après (min sans appel)
                    <input className={input} type="number" min={1} value={form.resetAfterMin} onChange={set("resetAfterMin")} />
                </label>
                <label className="text-xs text-gray-600">
                    Appels max par jour (0 = sans plafond)
                    <input className={input} type="number" min={0} value={form.maxPerDay} onChange={set("maxPerDay")} />
                </label>
                <label className="text-xs text-gray-600">
                    Établissements / résultats max par appel (1 à 20)
                    <input className={input} type="number" min={1} max={20} value={form.maxResults} onChange={set("maxResults")} />
                </label>
            </div>
            <div className="text-[11px] mb-3 rounded-lg bg-gray-50 border border-gray-100 p-2.5">
                {formError ? (
                    <span className="text-red-600">{formError}</span>
                ) : estimate ? (
                    <span className="text-gray-600">
                        {describeLimits(limits())}. Au plus vite : <strong>{n(estimate.callsPerDay)} appels/jour</strong> → {n(estimate.resultsPerDay)} établissements/jour
                        {estimate.daysForBase ? (
                            <>
                                {" "}
                                → toute la base ({n(estimate.base)} établissements) en <strong>{n(estimate.daysForBase)} jour{estimate.daysForBase > 1 ? "s" : ""}</strong> au minimum
                            </>
                        ) : null}
                        .
                    </span>
                ) : (
                    <span className="text-gray-400">Calcul…</span>
                )}
            </div>
            <label className="text-xs text-gray-600 block mb-3">
                Notes internes
                <textarea className={`${input} h-14`} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </label>
            <button onClick={() => save()} disabled={saving || !!formError} className="text-xs px-3 py-1.5 rounded-lg bg-black text-white disabled:opacity-50">
                {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
            {acc && (
                <p className="text-[11px] text-gray-400 mt-3">
                    {acc.enabledAt && <>Activé le {formatDateTime(acc.enabledAt)} par {acc.enabledBy}. </>}
                    {acc.disabledAt && <>Désactivé le {formatDateTime(acc.disabledAt)} par {acc.disabledBy}. </>}
                </p>
            )}
        </div>
    );
}

/** Where the limiter stands right now, and the button to release the client. */
function LimiterNow({ userId, detail, onDone }: { userId: string; detail: PublicApiAccountDetail; onDone: () => void }) {
    const acc = detail.account;
    if (!acc) return null;
    const r = acc.rate;
    const l = acc.limits;
    const nextCooldown = l.cooldownsMin.length ? l.cooldownsMin[Math.min(r.level, l.cooldownsMin.length - 1)] : 0;
    const unlock = async () => {
        if (!window.confirm("Débloquer ce client ? Sa pause, sa série en cours et son compteur du jour sont remis à zéro.")) return;
        const res = await unlockPublicApiAccount(userId);
        if (res.error) return window.alert(res.error);
        onDone();
    };
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-900">En ce moment</p>
                <button onClick={unlock} className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 hover:bg-gray-50">
                    Débloquer
                </button>
            </div>
            <div className={`text-sm font-medium mb-3 ${r.nextCallAllowed ? "text-green-700" : "text-red-600"}`}>
                {r.nextCallAllowed ? "Son prochain appel passera." : `Bloqué encore ${formatDuration(r.nextCallInSec)} (${REASON_LABELS[r.nextCallReason || ""] || r.nextCallReason}).`}
            </div>
            <dl className="grid grid-cols-2 gap-y-2 text-xs">
                <dt className="text-gray-400">Série en cours</dt>
                <dd className="text-gray-900 text-right">{l.burst > 0 ? `${r.series} / ${l.burst}` : "—"}</dd>
                <dt className="text-gray-400">Pauses prises (palier)</dt>
                <dd className="text-gray-900 text-right">{l.burst > 0 ? `${r.level} · prochaine ${nextCooldown} min` : "—"}</dd>
                <dt className="text-gray-400">En pause jusqu'à</dt>
                <dd className="text-gray-900 text-right">{r.lockedUntil ? new Date(r.lockedUntil).toLocaleTimeString("fr-FR") : "—"}</dd>
                <dt className="text-gray-400">Appels aujourd'hui</dt>
                <dd className="text-gray-900 text-right">
                    {n(r.callsToday)}
                    {l.maxPerDay > 0 && <span className="text-gray-400"> / {n(l.maxPerDay)}</span>}
                </dd>
                <dt className="text-gray-400">Dernier appel servi</dt>
                <dd className="text-gray-900 text-right">{formatRelative(r.lastCallAt)}</dd>
                <dt className="text-gray-400">Requête en cours</dt>
                <dd className="text-gray-900 text-right">{acc.requestInProgress ? "oui" : "non"}</dd>
            </dl>
        </div>
    );
}

function Deliveries({ userId }: { userId: string }) {
    const [rows, setRows] = useState<Awaited<ReturnType<typeof getPublicApiDeliveries>>["deliveries"]>([]);
    const [total, setTotal] = useState(0);
    const [next, setNext] = useState<string | null>(null);

    const load = async (before?: string) => {
        const res = await getPublicApiDeliveries(userId, before);
        setTotal(res.total);
        setNext(res.nextBefore);
        setRows((r) => (before ? [...r, ...res.deliveries] : res.deliveries));
    };
    useEffect(() => {
        load().catch(() => setRows([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-900">Ce que le client a reçu ({n(total)} établissements)</p>
                <p className="text-xs text-gray-400">Chaque établissement n'est compté qu'une fois, à sa première livraison.</p>
            </div>
            {rows.length === 0 ? (
                <p className="px-4 py-6 text-center text-xs text-gray-400">Rien pour l'instant.</p>
            ) : (
                <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-white">
                            <tr className="border-b border-gray-100 text-gray-500">
                                <th className="text-left px-4 py-2 font-medium">Reçu le</th>
                                <th className="text-left px-4 py-2 font-medium">Établissement</th>
                                <th className="text-left px-4 py-2 font-medium">SIRET</th>
                                <th className="text-left px-4 py-2 font-medium">Ville</th>
                                <th className="text-left px-4 py-2 font-medium">Via</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((d) => (
                                <tr key={d.establishmentId} className="border-b border-gray-50">
                                    <td className="px-4 py-1.5 text-gray-500 whitespace-nowrap">{formatDateTime(d.firstAt)}</td>
                                    <td className="px-4 py-1.5 text-gray-900">
                                        {d.name || "—"} {!d.active && <span className="text-[10px] text-gray-400">(fermé / masqué depuis)</span>}
                                    </td>
                                    <td className="px-4 py-1.5 font-mono text-gray-600">{d.siret || "—"}</td>
                                    <td className="px-4 py-1.5 text-gray-600">{[d.postcode, d.city].filter(Boolean).join(" ") || "—"}</td>
                                    <td className="px-4 py-1.5 font-mono text-gray-500">{d.endpoint}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {next && (
                <div className="px-4 py-2 border-t border-gray-100 text-center">
                    <button onClick={() => load(next)} className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50">
                        Plus
                    </button>
                </div>
            )}
        </div>
    );
}

function SmallTable({ title, head, rows }: { title: string; head: string[]; rows: React.ReactNode[][] }) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <p className="text-xs font-semibold text-gray-700 px-3 py-2 border-b border-gray-100">{title}</p>
            {rows.length === 0 ? (
                <p className="text-xs text-gray-400 px-3 py-3">—</p>
            ) : (
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-gray-400">
                            {head.map((h, i) => (
                                <th key={h} className={`px-3 py-1.5 font-medium ${i === 0 ? "text-left" : "text-right"}`}>
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i} className="border-t border-gray-50">
                                {r.map((c, j) => (
                                    <td key={j} className={`px-3 py-1.5 ${j === 0 ? "text-left text-gray-800 break-all" : "text-right text-gray-600 whitespace-nowrap"}`}>
                                        {c}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default function PublicApiAccount() {
    const { userId = "" } = useParams<{ userId: string }>();
    const [days, setDays] = useState(30);
    const [detail, setDetail] = useState<PublicApiAccountDetail | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = () =>
        getPublicApiAccount(userId, days)
            .then((d) => {
                setDetail(d);
                setError(null);
            })
            .catch((e) => setError(e?.response?.data?.error || "Impossible de charger ce client"));

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, days]);

    const series = useMemo(() => {
        if (!detail) return null;
        let coverage: number | null = null;
        const returned: { x: number; y: number }[] = [];
        const limited: { x: number; y: number | null }[] = [];
        const refused: { x: number; y: number | null }[] = [];
        const coveragePts: { x: number; y: number | null }[] = [];
        for (const c of detail.calls) {
            const x = new Date(c.at).getTime();
            if (c.deliveredAfter !== undefined && c.deliveredAfter !== null && c.baseSize) coverage = Math.round((c.deliveredAfter / c.baseSize) * 10000) / 100;
            returned.push({ x, y: c.establishments || 0 });
            limited.push({ x, y: c.status === 429 ? 0 : null });
            refused.push({ x, y: c.status >= 400 && c.status !== 429 ? 0 : null });
            coveragePts.push({ x, y: coverage });
        }
        return [
            { name: "Établissements renvoyés", type: "column", data: returned },
            { name: "Appels limités (429)", type: "scatter", data: limited },
            { name: "Autres refus (4xx/5xx)", type: "scatter", data: refused },
            { name: "Part de la base reçue (%)", type: "line", data: coveragePts },
        ];
    }, [detail]);

    const revoke = async (id: string, name: string) => {
        const reason = window.prompt(`Désactiver la clé « ${name} » ?\nRaison (facultatif) :`);
        if (reason === null) return;
        const res = await adminRevokeApiKey(id, reason);
        if (res.error) window.alert(res.error);
        load();
    };
    const restore = async (id: string, name: string) => {
        if (!window.confirm(`Réactiver la clé « ${name} » ?`)) return;
        const res = await adminRestoreApiKey(id);
        if (res.error) window.alert(res.error);
        load();
    };

    const acc = detail?.account;
    const totals = detail?.daily.reduce((s, d) => ({ calls: s.calls + d.calls, errors: s.errors + d.errors, flagged: s.flagged + d.flagged, delivered: s.delivered + d.delivered }), {
        calls: 0,
        errors: 0,
        flagged: 0,
        delivered: 0,
    });
    const limitedCount = detail?.calls.filter((c) => c.status === 429).length || 0;
    const rangeLabel = days === 1 ? "24 h" : `${days} j`;

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4 pb-12">
                <div className="mb-6">
                    <Link to="/bo/public-api" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900 mb-2">
                        <ArrowLeft className="w-3.5 h-3.5" /> API publique
                    </Link>
                    <div className="flex items-end justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-semibold text-gray-900">{detail?.user.email || "Client"}</h1>
                            <p className="text-sm text-gray-500 mt-0.5">
                                {[detail?.user.name, detail?.user.company].filter(Boolean).join(" · ") || "—"}
                                {acc && (
                                    <span className={`ml-2 px-2 py-0.5 rounded text-[11px] font-medium ${acc.enabled ? "bg-green-50 text-green-700 border border-green-100" : "bg-gray-100 text-gray-600"}`}>
                                        {acc.enabled ? "Accès actif" : "Accès désactivé"}
                                    </span>
                                )}
                                <Link to={`/bo/activity?user=${userId}`} className="ml-3 text-xs underline text-gray-500 hover:text-gray-900">
                                    Toute son activité dans l'app
                                </Link>
                            </p>
                        </div>
                        <div className="flex gap-1">
                            {RANGES.map((r) => (
                                <button key={r} onClick={() => setDays(r)} className={`px-2.5 py-1 rounded-lg text-xs font-medium ${days === r ? "bg-[#1A1A1A] text-white" : "bg-white border border-gray-200 text-gray-600"}`}>
                                    {r === 1 ? "24 h" : `${r} j`}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <BONavigation />

                {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
                {detail && (
                    <>
                        {!acc && (
                            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 text-sm text-gray-600">
                                Ce client n'a pas encore de compte API : il est créé automatiquement, accès actif, avec sa première clé (Clés API → Créer une clé).
                            </div>
                        )}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            <Card
                                label="Part de la base reçue"
                                value={acc ? `${acc.coverage.toLocaleString("fr-FR")} %` : "—"}
                                sub={
                                    acc ? (
                                        <>
                                            <ProgressBar percent={acc.coverage} />
                                            <span className="block mt-1">
                                                {n(acc.delivered)} / {n(acc.base)} établissements
                                            </span>
                                        </>
                                    ) : undefined
                                }
                            />
                            <Card label="Limitation" value={acc ? (acc.rate.nextCallAllowed ? "Libre" : `Bloqué ${formatDuration(acc.rate.nextCallInSec)}`) : "—"} tone={acc && !acc.rate.nextCallAllowed ? "danger" : undefined} sub={acc ? describeLimits(acc.limits) : undefined} />
                            <Card label={`Appels (${rangeLabel})`} value={n(totals?.calls)} sub={`${n(totals?.delivered)} établissements reçus pour la première fois · ${n(limitedCount)} limités (429)`} />
                            <Card label={`Appels signalés (${rangeLabel})`} value={n(totals?.flagged)} tone={totals && totals.flagged > 0 ? "danger" : "ok"} sub={acc ? `Au plus ${n(acc.estimate.callsPerDay)} appels/jour avec ces réglages` : undefined} />
                        </div>

                        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
                            <p className="text-sm font-semibold text-gray-900 mb-0.5">Chaque appel</p>
                            <p className="text-xs text-gray-400 mb-2">
                                Barres : établissements renvoyés par appel · vert : part de la base reçue après l'appel · points orange : appels limités (429) · points rouges : autres refus. Zoom à la souris.
                                {detail.calls.length >= 3000 && " 3 000 derniers appels de la période."}
                            </p>
                            <div className="h-80">
                                {detail.calls.length === 0 || !series ? (
                                    <p className="text-xs text-gray-400 pt-16 text-center">Aucun appel sur la période.</p>
                                ) : (
                                    <Chart type="line" width="100%" height="100%" options={perCallOptions()} series={series as any} />
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                            <div className="lg:col-span-2">
                                <LimitsEditor userId={userId} detail={detail} onSaved={load} />
                            </div>
                            <div className="flex flex-col gap-4">
                                <LimiterNow userId={userId} detail={detail} onDone={load} />
                                <div className="bg-white border border-gray-200 rounded-xl p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-2">Signaux</p>
                                    {detail.flags.length === 0 ? (
                                        <p className="text-xs text-gray-400">Aucun comportement suspect sur la période.</p>
                                    ) : (
                                        <div className="flex flex-col gap-1.5">
                                            {detail.flags.map((f) => (
                                                <div key={f.flag} className="flex items-center justify-between">
                                                    <FlagBadge flag={f.flag} count={f.count} />
                                                    <span className="text-[11px] text-gray-400">{formatRelative(f.lastAt)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-4">
                                <p className="text-sm font-semibold text-gray-900 mb-2">Par jour</p>
                                <div className="h-56">
                                    {detail.daily.length === 0 ? (
                                        <p className="text-xs text-gray-400 pt-10 text-center">—</p>
                                    ) : (
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={dailyOptions(detail.daily.map((d) => d.day.slice(5)))}
                                            series={[
                                                { name: "Appels servis", data: detail.daily.map((d) => d.calls - d.errors) },
                                                { name: "Appels refusés", data: detail.daily.map((d) => d.errors) },
                                                { name: "Établissements reçus (1re fois)", data: detail.daily.map((d) => d.delivered) },
                                            ]}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                                <p className="text-sm font-semibold text-gray-900 px-4 py-3 border-b border-gray-100">Clés (la limitation est commune à toutes)</p>
                                {detail.keys.length === 0 ? (
                                    <p className="text-xs text-gray-400 px-4 py-3">Aucune clé créée (Paramètres → Accès API, côté client).</p>
                                ) : (
                                    <ul>
                                        {detail.keys.map((k) => (
                                            <li key={k._id} className={`px-4 py-2.5 border-b border-gray-50 text-xs ${k.revokedAt ? "opacity-60" : ""}`}>
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="min-w-0">
                                                        <p className="text-gray-900 truncate">{k.name}</p>
                                                        <p className="text-gray-400 font-mono">{k.prefix}…</p>
                                                    </div>
                                                    {k.revokedAt ? (
                                                        <button onClick={() => restore(k._id, k.name)} className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900">
                                                            <RotateCcw className="w-3.5 h-3.5" /> Réactiver
                                                        </button>
                                                    ) : (
                                                        <button onClick={() => revoke(k._id, k.name)} className="px-2 py-1 rounded-sm border border-red-200 text-red-600 hover:bg-red-50">
                                                            Désactiver
                                                        </button>
                                                    )}
                                                </div>
                                                <p className="text-gray-500 mt-1">
                                                    {n(k.usageCount)} appels · dernier {formatRelative(k.lastUsedAt)}
                                                    {k.lastUsedIp && <span className="font-mono"> · {k.lastUsedIp}</span>}
                                                    {k.revokedAt && <> · {k.revokedBy === "user" ? "supprimée par le client" : `désactivée par ${k.revokedBy}`}</>}
                                                </p>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                            <SmallTable title="Endpoints" head={["Endpoint", "Appels", "Erreurs", "Reçus (1re fois)", "Dernier"]} rows={detail.endpoints.map((e) => [e.endpoint, n(e.calls), n(e.errors), n(e.delivered), formatRelative(e.lastAt)])} />
                            <SmallTable title="Codes d'erreur" head={["Code", "Fois", "Dernier"]} rows={detail.errors.map((e) => [e.code, n(e.count), formatRelative(e.lastAt)])} />
                            <SmallTable title="Adresses IP" head={["IP", "Appels", "Première", "Dernière"]} rows={detail.ips.map((i) => [i.ip || "?", n(i.calls), formatRelative(i.firstAt), formatRelative(i.lastAt)])} />
                            <SmallTable title="Outils (User-Agent)" head={["User-Agent", "Appels", "Dernier"]} rows={detail.userAgents.map((u) => [u.userAgent || "(aucun)", n(u.calls), formatRelative(u.lastAt)])} />
                        </div>

                        <div className="mb-6">
                            <CallsTable userId={userId} showUser={false} title="Tous ses appels" />
                        </div>

                        <Deliveries userId={userId} />
                    </>
                )}
            </div>
        </div>
    );
}
